import { useState } from 'react';

export default function Home() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const systems = [
    {
      id: 1,
      badge: '步驟 1',
      icon: '📋',
      title: '通報表製作子系統',
      description: '產出經費申請 PDF，列印後核章備用',
      url: 'https://warm-notify-desk.lovable.app',
      color: 'from-emerald-400/20 to-teal-400/20',
      borderColor: 'border-emerald-400/30',
      hoverColor: 'hover:border-emerald-500'
    },
    {
      id: 2,
      badge: '步驟 2',
      icon: '🏫',
      title: '主系統',
      description: '上傳核章後文件，完成線上回報通報',
      url: 'https://nantou-school-aid-hub.lovable.app',
      color: 'from-amber-400/20 to-orange-400/20',
      borderColor: 'border-amber-400/30',
      hoverColor: 'hover:border-amber-500'
    },
    {
      id: 3,
      badge: '步驟 3',
      icon: '✅',
      title: '成果回報表製作子系統',
      description: '工程完竣後，產出成果報告進行結案',
      url: 'https://sunnyreport-generator.lovable.app',
      color: 'from-blue-400/20 to-cyan-400/20',
      borderColor: 'border-blue-400/30',
      hoverColor: 'hover:border-blue-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 font-['Noto_Sans_TC']">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-green-100/50 sticky top-0 z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-center sm:justify-start">
          <div className="flex items-center gap-3 sm:gap-4">
            <img 
              src="https://static.readdy.ai/image/0df4aaf108c84a534211ee2934b2e4bf/2f2ba8438f2cc6d697c6ea0c3e3fddfb.png" 
              alt="南投縣政府" 
              className="h-10 sm:h-12 w-auto object-contain"
            />
            <div className="h-8 sm:h-10 w-px bg-green-200"></div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-800">南投縣國中小校園</h1>
              <p className="text-xs sm:text-sm text-gray-600">搶修搶險災損線上回報區</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-5xl font-semibold text-gray-800 mb-4 sm:mb-6 leading-tight px-4">
              南投縣國中小校園<br />
              <span className="text-emerald-600">搶修搶險災損線上回報區</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              整合通報、回報、成果管理三大系統，提供完整的校園災損處理流程，讓您輕鬆完成從通報到結案的每一步驟
            </p>
          </div>

          {/* Workflow Section */}
          <div className="mb-16 sm:mb-20">
            <div className="flex items-center justify-center gap-3 mb-8 sm:mb-12">
              <div className="h-px w-12 sm:w-16 bg-gradient-to-r from-transparent to-emerald-300"></div>
              <h3 className="text-2xl sm:text-3xl font-semibold text-gray-800">作業流程</h3>
              <div className="h-px w-12 sm:w-16 bg-gradient-to-l from-transparent to-emerald-300"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto px-4">
              {[
                {
                  step: '1',
                  title: '通報表製作',
                  items: ['產出 PDF', '列印核章'],
                  icon: '📝',
                  color: 'bg-gradient-to-br from-emerald-100 to-teal-100',
                  ring: 'ring-4 ring-emerald-200/50'
                },
                {
                  step: '2',
                  title: '主系統上傳',
                  items: ['上傳文件', '完成通報'],
                  icon: '📤',
                  color: 'bg-gradient-to-br from-amber-100 to-orange-100',
                  ring: 'ring-4 ring-amber-200/50'
                },
                {
                  step: '3',
                  title: '成果回報',
                  items: ['完成後製作', '提交結案'],
                  icon: '✨',
                  color: 'bg-gradient-to-br from-blue-100 to-cyan-100',
                  ring: 'ring-4 ring-blue-200/50'
                }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className={`${item.color} ${item.ring} rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-white/80 h-full transform hover:scale-105`}>
                    <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/90 flex items-center justify-center text-3xl sm:text-4xl shadow-md">
                        {item.icon}
                      </div>
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/90 flex items-center justify-center font-bold text-xl sm:text-2xl text-gray-700 shadow-md">
                        {item.step}
                      </div>
                    </div>
                    <h4 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">{item.title}</h4>
                    <ul className="space-y-2">
                      {item.items.map((text, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-700">
                          <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                          <span className="text-base sm:text-lg font-medium">{text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-4xl text-emerald-500 drop-shadow-lg"></i>
                    </div>
                  )}
                  {index < 2 && (
                    <div className="md:hidden flex justify-center my-4">
                      <i className="ri-arrow-down-line text-4xl text-emerald-500 drop-shadow-lg"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Systems Section */}
          <div>
            <div className="flex items-center justify-center gap-3 mb-8 sm:mb-12">
              <div className="h-px w-12 sm:w-16 bg-gradient-to-r from-transparent to-emerald-300"></div>
              <h3 className="text-2xl sm:text-3xl font-semibold text-gray-800">系統入口</h3>
              <div className="h-px w-12 sm:w-16 bg-gradient-to-l from-transparent to-emerald-300"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto px-4">
              {systems.map((system) => (
                <div
                  key={system.id}
                  className="relative group"
                  onMouseEnter={() => setHoveredCard(system.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className={`bg-white rounded-2xl p-6 sm:p-8 border-2 ${system.borderColor} ${system.hoverColor} transition-all duration-300 shadow-sm hover:shadow-xl h-full flex flex-col ${hoveredCard === system.id ? 'transform scale-105' : ''}`}>
                    <div className="absolute top-4 sm:top-6 right-4 sm:right-6">
                      <span className="px-3 py-1 bg-gradient-to-r ${system.color} rounded-full text-sm font-medium text-gray-700 border border-white/50">
                        {system.badge}
                      </span>
                    </div>
                    
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${system.color} flex items-center justify-center text-3xl sm:text-4xl mb-4 sm:mb-6 shadow-sm border border-white/50`}>
                      {system.icon}
                    </div>
                    
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 leading-snug">
                      {system.title}
                    </h4>
                    
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed flex-grow">
                      {system.description}
                    </p>
                    
                    <a
                      href={system.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 font-medium shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
                    >
                      <span>前往系統</span>
                      <i className="ri-external-link-line text-lg"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-emerald-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-teal-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-amber-200/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <h5 className="text-lg font-semibold mb-3 sm:mb-4">關於系統</h5>
              <p className="text-emerald-50 text-sm sm:text-base leading-relaxed">
                提供南投縣國中小校園完整的災損通報與管理解決方案
              </p>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-3 sm:mb-4">聯絡資訊</h5>
              <p className="text-emerald-50 text-sm sm:text-base leading-relaxed">
                如有任何問題，請聯繫教育處國教科承辦人員
              </p>
            </div>
          </div>
          <div className="border-t border-emerald-400/30 pt-4 sm:pt-6 text-center">
            <p className="text-emerald-50 text-xs sm:text-sm">
              All rights reserved by 傑巴 · 多傑
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
